/**
 *     ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { GslbApiService, HealthChecksApiService } from '../../../../http';
import { GslbFrontendModel, TemplateModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { globalNamesDomainValidation } from '../../../../directives';
import { hostnameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-gslb-frontend',
    templateUrl: './gslb-frontend.component.html'
})
export class GslbFrontendComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Monitor codes dom reference
     */
    @ViewChildren('codes')
    public codes: QueryList<any> = new QueryList<any>();

    /**
     * Add monitor code dom reference.
     */
    @ViewChild('addCodeButton', { static: true })
    public addCodeButton: ElementRef;

    /**
     * Our available made global names from the API.
     */
    public globalNames: GslbFrontendModel[] = [];

    /**
     * Store the edit id.
     */
    public editId: string;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        backEndMonitor: [],
        backEndMonitorRetries: [],
        backendMonitorStatus: [],
        healthCheckScripts: [],
        backendLbMethod: []
    };

    /**
     * Store the list of healthcheck scripts.
     */
    public healthCheckScripts: any[];

    /**
     * Private variable for monitor code getter/setter.
     * @private
     */
    private _monitorCodes: FormArray;

    constructor(
        private gslbApiService: GslbApiService,
        private healthChecksApiService: HealthChecksApiService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
    }

    ngOnInit(): void {
        this.componentTitle = WizardTitleEnum.FRONTENDS;
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Getter for expected monitor codes in the form.
     */
    get monitorCodes(): FormArray {
        this._monitorCodes = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorExpectedCodes') as FormArray;

        return this._monitorCodes;
    }

    /**
     * Add a monitor code row to the form.
     */
    public addMonitorCode(): void {
        this.monitorCodes.push(new FormControl('', [Validators.required]));
        this.focusNewMonitorCodeInput();
    }

    /**
     * Remove monitor code field from form.
     * @param index
     */
    public removeMonitorCode(index: number): void {
        this.monitorCodes.removeAt(index);
        this.focusNewMonitorCodeInput();
        this.validateMonitorCodes();
    }

    /**
     * Enable and disables the add button for monitor codes.
     * @param index
     */
    public monitorCodeIsInvalid(index: number): boolean {
        return this.monitorCodes.controls[index].invalid;
    }

    /**
     * Focus cursor to the last/new monitor code field on the form.
     * @private
     */
    private focusNewMonitorCodeInput(): void {
        const timerId = setTimeout((): void => {
            this.codes.last.nativeElement.focus();
            clearTimeout(timerId);
        }, 50);
    }

    /**
     * Validate monitor codes.
     */
    public validateMonitorCodes(): void {
        const duplicates: any[] = [];

        for (const control of this.monitorCodes.controls) {
            if (duplicates.includes(control.value)) {
                control.setErrors({ 'duplicateValidator': 'code is in use.' });
                control.markAsTouched();
            } else {
                if (control.hasError('duplicateValidator')) {
                    control.setErrors({ 'duplicateValidator': null });
                    control.updateValueAndValidity();
                }
            }
            duplicates.push(control.value);
        }
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setupFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.DOMAIN_NAME]: this.formBuilder.group({
                // General
                id: [''], // <- Used when editing.
                domain: [null, [Validators.required, globalNamesDomainValidation]],
                lbMethod: ['wrr'],
                timeToLive: [null, [Validators.required, Validators.min(0)]],
                fallback: [],
                maxAddrsReturned: [1, [Validators.min(1), Validators.max(1024)]]
            }),
            [WizardSubStepTitlesEnum.HEALTHCHECK]: this.formBuilder.group({
                monitor: ['http', []],
                monitorURLPath: ['/', Validators.required],
                monitorHostname: ['', hostnameValidation],
                monitorPort: [80, [Validators.required, portRangeValidation]],
                monitorExpectedCodes: new FormArray([new FormControl(200, [Validators.required])]),
                monitorSendString: [''],
                monitorMatchRe: [''],
                monitorStatus: ['up'],
                monitorExternalScriptID: [{ value: null, disabled: true }],
                monitorInterval: [1, [Validators.min(1), Validators.max(3600)]],
                monitorTimeout: [100, [Validators.min(100), Validators.max(10000)]],
                monitorRetries: [1]
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.FRONTENDS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    // Validate the form each step of the way. Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('global-names', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.DOMAIN_NAME + '.domain').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: string): void => {
                    this.checkNameForDuplicates(name, this.globalNames, 'domain');
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitor').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(mode => {
                this.inputStateStore['monitorExpectedCodes'] = { hidden: mode !== 'https' };
                this.monitorChange();
            });
    }

    /**
     * Monitor changes in the pool form.
     */
    public monitorChange(): void {
        this.disablePoolsFormControls();

        switch (this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitor').value) {
            case 'http':
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorHostname').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorURLPath').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorPort').enable();
                break;
            case 'https':
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorURLPath').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorHostname').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorPort').enable();
                break;
            case 'tcp':
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorPort').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorSendString').enable();
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorMatchRe').enable();
                break;
            case 'forced':
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorStatus').enable();
                break;
            case 'external':
            case 'external_weight':
                this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorExternalScriptID').enable();
        }
    }

    /**
     * Disabled pools form controls.
     * @private
     */
    private disablePoolsFormControls(): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorHostname').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorURLPath').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorPort').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorSendString').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorMatchRe').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorStatus').disable();
        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTHCHECK + '.monitorExternalScriptID').disable();
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        /**
         * Advanced Mode Subscriber
         * Note: We subscribe to whether the advanced mode button has been toggled.
         */
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                advancedState => {
                    this.advancedMode = advancedState;
                    this.toggleAdvanceModeInputs();
                }
            );
    }

    /**
     * Toggle the inputs that are only part of advanced mode.
     * @private
     */
    private toggleAdvanceModeInputs(): void {
        const advancedMode = this.advancedMode === 'advanced';
        // General step
        this.inputStateStore['fallback'] = { hidden: !advancedMode };
        this.inputStateStore['maxAddrsReturned'] = { hidden: !advancedMode };
        // Healthcheck step
        this.inputStateStore['monitorHostname'] = { hidden: !advancedMode };
        this.inputStateStore['monitorInterval'] = { hidden: !advancedMode };
        this.inputStateStore['monitorTimeout'] = { hidden: !advancedMode };
        this.inputStateStore['monitorRetries'] = { hidden: !advancedMode };
        this.inputStateStore['monitorExpectedCodes'] = { hidden: !advancedMode };
    }

    /**
     * Loads both the floating IP groups and Interfaces and set the values.
     * @private
     */
    private initLoading(): void {
        forkJoin({
            healthCheckScripts: this.healthChecksApiService.getAllHealthChecks(),
            globalNames: this.gslbApiService.getFrontends()
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: (dataGroup: any): void => {
                        const serviceError: any = this.getErrorForService(WizardTitleEnum.FRONTENDS);
                        const template: any = this.getTemplate(WizardTitleEnum.FRONTENDS, serviceError);

                        const formattedGlobalNames: any[] = this.formatServices(dataGroup['globalNames'], WizardTitleEnum.FRONTENDS);
                        this.globalNames = this.filterServicesByName(template, formattedGlobalNames, 'domain');

                        if (dataGroup['healthCheckScripts']?.length) {
                            this.healthCheckScripts = this.filterCompatibleHealthcheckScripts(dataGroup['healthCheckScripts']);
                        }

                        this.patchTemplateToForm(template);
                        // Once everything is loaded check to see if there are any error in the controller state
                        if (serviceError?.error?.length) {
                            this.setErrorStateOnPage(serviceError.error);
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'GlobalNamesComponent:initLoading',
                            'Unable to get global name from the API', error.message
                        );
                    }
                }
            );
    }

    /**
     * Filter the list of healthcheck scripts that are GSLB compatible.
     * @param hcScripts
     * @private
     */
    private filterCompatibleHealthcheckScripts(hcScripts: any[]): any[] {
        return hcScripts.filter(item => item.services.includes('gslb'));
    }

    /**
     * Initialises the form values.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent();

        // Populate our patch
        const templatePatch: any = this.createTemplatePatch(template);

        // Check if we are in advanced mode and set the correct states on the inputs before patching
        this.advancedMode = this.wizardService.wizardMode;

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.FRONTENDS);

        // If a property is set as null in the template remove the property so the default if used.
        this.removeNullValueProperties(templatePatch);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        // Check the name after patching
        this.checkNameForDuplicates(template.domain?.value, this.globalNames, 'domain', true);

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();
    }

    /**
     * Check for error state.
     * @param stepError
     * @private
     */
    private setErrorStateOnPage(stepError: any[]): void {
        this.loadedWithErrors(stepError);
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.backEndMonitor = [
            <DropdownModel>{ key: 'http', value: 'HTTP' },
            <DropdownModel>{ key: 'https', value: 'HTTPS' },
            <DropdownModel>{ key: 'tcp', value: 'TCP' },
            <DropdownModel>{ key: 'external', value: 'External' },
            <DropdownModel>{ key: 'external_weight', value: 'External Dynamic Weight' },
            <DropdownModel>{ key: 'forced', value: 'Forced' },
        ];

        const retries: number[] = [0, 1, 2, 3, 4, 5];
        this.dropdown.backEndMonitorRetries = this.utilsService.convertArrayToDropDown(retries);
        this.dropdown.backendMonitorStatus = [
            <DropdownModel>{ key: 'up', value: 'Up' },
            <DropdownModel>{ key: 'down', value: 'Down' },
        ];

        this.dropdown.backendLbMethod = [
            <DropdownModel>{ key: 'wrr', value: 'Weighted round robin' },
            <DropdownModel>{ key: 'twrr', value: 'Topology weighted round robin' },
            <DropdownModel>{ key: 'fogroup', value: 'Failover Group' },
        ];

        this.dropdown.healthCheckScripts = this.getUpHCScriptsDropdown();
    }

    /**
     * Get healthcheck scripts dropdown data.
     * @private
     */
    private getUpHCScriptsDropdown(): DropdownModel[] {
        const scripts: DropdownModel[] = [];
        for (const script of this.healthCheckScripts) {
            scripts.push(<DropdownModel>{ key: script.id, value: script.name });
        }

        return scripts;
    }

    /**
     * Format form values for output.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const outputObject = this.flattenServiceFormValues(<GslbFrontendModel>{});

            // loop through form values
            const template: TemplateModel = this.wizardService.controllerState[WizardTitleEnum.FRONTENDS][this.templateIndex];
            for (const property in outputObject) {
                if (outputObject.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: outputObject[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }
}
